import React, { useState, useEffect } from 'react';
import { BookOpen, Eye, Heart, ChevronRight, Twitter, Github, Linkedin, Globe, Mail, MapPin, Image } from 'lucide-react';
import { formatReadTime } from '../../utils';
import { supabase } from '../../supabaseClient';
import { useTheme } from '../../ThemeContext';
import AttestBadge from '../AttestBadge';


export default function AuthorProfileView({ 
  selectedAuthor,
  posts,
  walletAddress,
  setView,
  viewPost
}) {
  const { t, isDark } = useTheme();
  const [socialLinks, setSocialLinks] = useState([]);
  const [followingCount, setFollowingCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoadingFollow, setIsLoadingFollow] = useState(false);

  // Load social links for this author
  useEffect(() => {
    const loadSocialLinks = async () => {
      if (!selectedAuthor?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('social_links')
          .select('*')
          .eq('entity_type', 'individual')
          .eq('entity_id', selectedAuthor.id);
        
        if (!error && data) {
          setSocialLinks(data);
        }
      } catch (error) {
        console.error('Error loading social links:', error);
      }
    };
    
    loadSocialLinks();
  }, [selectedAuthor?.id]);

  // Load following count (how many this author follows)
  useEffect(() => {
    const loadFollowingCount = async () => {
      if (!selectedAuthor?.id) return;
      
      try {
        const { count, error } = await supabase
          .from('follows')
          .select('*', { count: 'exact', head: true })
          .eq('follower_entity_type', 'individual')
          .eq('follower_entity_id', selectedAuthor.id);
        
        if (!error) {
          setFollowingCount(count || 0);
        }
      } catch (error) {
        console.error('Error loading following count:', error);
      }
    };
    
    loadFollowingCount();
  }, [selectedAuthor?.id]);

  // Check if current user is following this author
  useEffect(() => {
    const checkFollowStatus = async () => {
      if (!walletAddress || !selectedAuthor?.id) return;
      
      // Get current user's authors to check if any are following
      try {
        const { data: userAuthors, error: authorsError } = await supabase
          .from('authors')
          .select('id')
          .eq('wallet_address', walletAddress);
        
        if (authorsError || !userAuthors || userAuthors.length === 0) return;
        
        // Check if any of user's authors follow this author
        const { data: followData, error: followError } = await supabase
          .from('follows')
          .select('id')
          .eq('follower_entity_type', 'individual')
          .in('follower_entity_id', userAuthors.map(a => a.id))
          .eq('following_entity_type', 'individual')
          .eq('following_entity_id', selectedAuthor.id)
          .limit(1);
        
        if (!followError) {
          setIsFollowing(followData && followData.length > 0);
        }
      } catch (error) {
        console.error('Error checking follow status:', error);
      }
    };
    
    checkFollowStatus();
  }, [walletAddress, selectedAuthor?.id]);

  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!walletAddress) {
      alert('Please connect your wallet to follow authors');
      return;
    }
    
    setIsLoadingFollow(true);
    
    try {
      // Get user's first author (for now, use first author to follow)
      const { data: userAuthors, error: authorsError } = await supabase
        .from('authors')
        .select('id')
        .eq('wallet_address', walletAddress)
        .limit(1);
      
      if (authorsError || !userAuthors || userAuthors.length === 0) {
        alert('Please create an author profile first');
        setIsLoadingFollow(false);
        return;
      }
      
      const userAuthorId = userAuthors[0].id;
      
      if (isFollowing) {
        // Unfollow
        const { error } = await supabase
          .from('follows')
          .delete()
          .eq('follower_entity_type', 'individual')
          .eq('follower_entity_id', userAuthorId)
          .eq('following_entity_type', 'individual')
          .eq('following_entity_id', selectedAuthor.id);
        
        if (!error) {
          setIsFollowing(false);
        }
      } else {
        // Follow
        const followId = `follow_${Date.now()}`;
        const { error } = await supabase
          .from('follows')
          .insert({
            id: followId,
            follower_entity_type: 'individual',
            follower_entity_id: userAuthorId,
            following_entity_type: 'individual',
            following_entity_id: selectedAuthor.id,
            created_at: new Date().toISOString()
          });
        
        if (!error) {
          setIsFollowing(true);
        }
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    }
    
    setIsLoadingFollow(false);
  };

  // Navigate to following page
  const viewFollowing = () => {
    // TODO: Implement FollowingView navigation
    console.log('Navigate to following page for author:', selectedAuthor.id);
  };

  // Get icon for social link type
  const getSocialIcon = (type) => {
    const iconProps = { size: 20, style: { color: t.text } };
    switch (type.toLowerCase()) {
      case 'twitter':
      case 'x':
        return <Twitter {...iconProps} />;
      case 'github':
        return <Github {...iconProps} />;
      case 'linkedin':
        return <Linkedin {...iconProps} />;
      case 'website':
        return <Globe {...iconProps} />;
      case 'email':
        return <Mail {...iconProps} />;
      default:
        return <Globe {...iconProps} />;
    }
  };

  if (!selectedAuthor) return null;

  // Filter posts for this author using entity-based fields
  const authorPosts = posts.filter(p => 
    p.entity_type === 'individual' && p.entity_id === selectedAuthor.id
  );

  const isOwnProfile = selectedAuthor.wallet_address === walletAddress;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      {/* Back Button */}
      <button
        onClick={() => {
          // Check if we came from a post
          const params = new URLSearchParams(window.location.search);
          const fromPost = params.get('from') === 'post';
          const postId = params.get('post');
          
          if (fromPost && postId) {
            // Return to the specific post
            window.history.pushState({}, '', `?post=${postId}&from=author`);
            setView('reader');
          } else if (isOwnProfile) {
            // If it's user's own profile, go to wallet
            setView('profile');
          } else {
            // Otherwise go to home
            setView('home');
            window.history.pushState({}, '', '/');
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: t.text,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          marginBottom: '40px',
          fontFamily: 'Poppins, sans-serif',
          padding: '0'
        }}
      >
        <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
        {(() => {
          const params = new URLSearchParams(window.location.search);
          const fromPost = params.get('from') === 'post';
          if (fromPost) return 'Back to post';
          if (isOwnProfile) return 'Back to Profile';
          return 'Back to home';
        })()}
      </button>

      {/* Profile Header - Center Aligned */}
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        {/* Avatar */}
        {selectedAuthor.avatar_url ? (
          <img
            src={selectedAuthor.avatar_url}
            alt={selectedAuthor.name}
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              objectFit: 'cover',
              margin: '0 auto 20px',
              border: `2px solid ${t.accent}`
            }}
          />
        ) : (
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: t.buttonBg,
            color: t.buttonText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
            fontWeight: '600',
            margin: '0 auto 20px',
            fontFamily: 'Poppins, sans-serif'
          }}>
            {selectedAuthor.name[0].toUpperCase()}
          </div>
        )}

        {/* Name */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: '600',
          marginBottom: '12px',
          fontFamily: 'Poppins, sans-serif',
          color: t.text
        }}>
          {selectedAuthor.name}
        </h1>

        {/* Bio */}
        {selectedAuthor.bio && (
          <p style={{
            fontSize: '16px',
            color: t.textSecondary,
            marginBottom: '20px',
            maxWidth: '500px',
            margin: '0 auto 20px',
            lineHeight: '1.6',
            fontFamily: 'Poppins, sans-serif'
          }}>
            {selectedAuthor.bio}
          </p>
        )}

        {/* Social Links */}
        {socialLinks.length > 0 && (
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            marginBottom: '20px'
          }}>
            {socialLinks.map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  borderRadius: '6px',
                  border: `1px solid ${t.borderStrong}`,
                  backgroundColor: 'transparent',
                  transition: 'all 0.2s',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = t.accent;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = t.borderStrong;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {getSocialIcon(link.type)}
              </a>
            ))}
          </div>
        )}

        {/* Stats */}
        <div style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '14px',
          color: t.textSecondary,
          fontFamily: 'Poppins, sans-serif',
          marginBottom: '24px'
        }}>
          <span>{authorPosts.length} post{authorPosts.length !== 1 ? 's' : ''}</span>
          <span>·</span>
          <button
            onClick={viewFollowing}
            style={{
              background: 'none',
              border: 'none',
              color: t.textSecondary,
              cursor: 'pointer',
              padding: '0',
              textDecoration: 'underline',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '14px'
            }}
            onMouseEnter={(e) => e.target.style.color = t.text}
            onMouseLeave={(e) => e.target.style.color = '#78716c'}
          >
            Following {followingCount}
          </button>
        </div>

        {/* Follow Button - only show if not own profile */}
        {!isOwnProfile && (
          <button
            onClick={handleFollowToggle}
            disabled={isLoadingFollow}
            style={{
              backgroundColor: isFollowing ? 'transparent' : t.buttonBg,
              color: isFollowing ? t.buttonBg : t.cream,
              padding: '10px 24px',
              borderRadius: '12px',
              border: isFollowing ? `1px solid ${t.accent}` : 'none',
              cursor: isLoadingFollow ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s',
              opacity: isLoadingFollow ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoadingFollow) {
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {isLoadingFollow ? 'Loading...' : (isFollowing ? 'Following' : 'Follow')}
          </button>
        )}
      </div>

      {/* Divider */}
      <div style={{
        height: '1px',
        backgroundColor: t.borderStrong,
        marginBottom: '48px'
      }} />

      {/* Posts Section */}
      <div>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '32px',
          fontFamily: 'Poppins, sans-serif',
          color: t.text
        }}>
          Recent Posts
        </h2>

        {authorPosts.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {authorPosts.map((post) => (
              <article 
                key={post.id}
                onClick={() => viewPost(post)}
                style={{
                  cursor: 'pointer',
                  padding: '24px',
                  borderRadius: '8px',
                  backgroundColor: t.surface,
                  border: `1px solid ${t.borderStrong}`,
                  transition: 'all 0.2s',
                  display: 'flex',
                  gap: '20px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = t.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = t.borderStrong;
                }}
              >
                {/* Thumbnail */}
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '8px',
                  backgroundColor: post.featured_image_url ? 'transparent' : '#e7e5e4',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  {post.featured_image_url ? (
                    <img
                      src={post.featured_image_url}
                      alt={post.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <Image size={32} style={{ color: t.text, opacity: 0.3 }} />
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  {/* Category Tag */}
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '999px',
                    border: `1px solid ${t.borderStrong}`,
                    fontSize: '11px',
                    fontWeight: '500',
                    color: t.text,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontFamily: 'Poppins, sans-serif',
                    marginBottom: '12px'
                  }}>
                    {post.category}
                  </span>

                  {/* Title */}
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: t.text,
                    fontFamily: 'Poppins, sans-serif',
                    lineHeight: '1.3'
                  }}>
                    {post.title}
                  </h3>

                  {/* Subtitle */}
                  {post.subtitle && (
                    <p style={{
                      fontSize: '14px',
                      color: t.textSecondary,
                      marginBottom: '12px',
                      lineHeight: '1.5',
                      fontFamily: 'Poppins, sans-serif'
                    }}>
                      {post.subtitle}
                    </p>
                  )}

                  {/* Meta Info */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    fontSize: '13px',
                    color: t.textSecondary,
                    fontFamily: 'Poppins, sans-serif'
                  }}>
                    <span>{formatReadTime(post.read_time)}</span>
                    <span>·</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Eye size={14} />
                      {post.views || 0}
                    </span>
                    {post.attest_tx_hash && (
                      <AttestBadge txHash={post.attest_tx_hash} />
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px', 
            color: t.textSecondary,
            fontFamily: 'Poppins, sans-serif'
          }}>
            <BookOpen size={48} style={{ 
              margin: '0 auto 16px', 
              opacity: 0.3,
              color: t.text
            }} />
            <p style={{ fontSize: '16px' }}>No posts yet</p>
          </div>
        )}
      </div>
    </div>
  );
}